import {useCallback, useEffect, useState} from "react";
import {useParams, useNavigate} from "react-router-dom";
import {Box, Button, HStack, IconButton, Image, Spinner, Table, Text, VStack} from "@chakra-ui/react";
import {HiArrowRight} from "react-icons/hi2";
import ModelService from "@/services/agency/ModelService";
import AssignScenarioModal from "@/pages/models/components/AssignScenarioModal";
import {useAuth} from "@/auth/AuthContext";

const ModelDetailPage = () => {
    const {id} = useParams();
    const navigate = useNavigate();
    const {currentUser} = useAuth();
    const [agent, setAgent] = useState(null);
    const [scenarios, setScenarios] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);

    const fetchAgent = useCallback(() => {
        setIsLoading(true);
        const fetch = currentUser?.role === 'Admin'
            ? ModelService.getByIdAdmin(id)
            : ModelService.getById(id);
        fetch.then((data) => {
            setAgent(data);
            setScenarios(data?.scenarios ?? []);
        }).finally(() => setIsLoading(false));
    }, [id, currentUser?.role]);

    useEffect(() => {
        fetchAgent();
    }, [fetchAgent]);

    if (isLoading) {
        return (
            <VStack w="full" py={12} align="center">
                <Spinner color="white" size="xl"/>
            </VStack>
        );
    }

    if (!agent) {
        return (
            <VStack w="full" py={12} align="center">
                <Text color="text.grey">Agent not found.</Text>
            </VStack>
        );
    }

    return (
        <VStack w="full" py={6} gap={6} align="flex-start">
            <Box w="full" bg="bg.card" border="1px solid" borderColor="border.primary" borderRadius={12} p={5}>
                <HStack gap={4} align="flex-start">
                    <Image
                        src={agent.avatarUrl ?? "https://placehold.co/80x80/22c55e/white?text=AI"}
                        boxSize="80px"
                        borderRadius={8}
                        objectFit="cover"
                        flexShrink={0}
                    />
                    <VStack align="flex-start" gap={2}>
                        <Text fontWeight={700} fontSize="xl" color="white">{agent.name}</Text>
                        <Text color="text.grey" fontSize="sm" maxW="520px" lineHeight={1.6}>{agent.description}</Text>
                    </VStack>
                </HStack>
            </Box>

            <VStack w="full" align="flex-start" gap={3}>
                <HStack w="full" justify="space-between">
                    <Text fontWeight={700} fontSize="lg" color="white">Scenarios</Text>
                    <Button variant="primary" size="sm" onClick={() => setIsAssignModalOpen(true)}>
                        Przypisz scenariusz
                    </Button>
                </HStack>
                <Box w="full" border="1px solid" borderColor="border.primary" borderRadius={12} overflowX="auto">
                    <Table.Root minW="640px">
                        <Table.Header>
                            <Table.Row bg="bg.card" borderBottom="1px solid" borderColor="border.primary">
                                {['Scenario name', 'Edited at', 'Created at', 'Actions'].map((col, i) => (
                                    <Table.ColumnHeader
                                        key={col}
                                        color="text.grey"
                                        fontWeight={600}
                                        fontSize="xs"
                                        textTransform="uppercase"
                                        letterSpacing="wider"
                                        py={3}
                                        px={5}
                                        border="none"
                                        textAlign={i === 3 ? 'right' : 'left'}
                                    >
                                        {col}
                                    </Table.ColumnHeader>
                                ))}
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {scenarios.map((scenario) => (
                                <Table.Row
                                    key={scenario.id}
                                    bg="bg.secondary"
                                    borderBottom="1px solid"
                                    borderColor="border.primary"
                                    _hover={{bg: "bg.highlight"}}
                                    transition="background 0.15s"
                                >
                                    <Table.Cell py={4} px={5} border="none">
                                        <Text color="white" fontSize="sm" fontWeight={500}>{scenario.name}</Text>
                                    </Table.Cell>
                                    <Table.Cell py={4} px={5} border="none">
                                        <Text color="text.grey" fontSize="sm">
                                            {new Date(scenario.editedAtUtc).toLocaleDateString('pl-PL')}
                                        </Text>
                                    </Table.Cell>
                                    <Table.Cell py={4} px={5} border="none">
                                        <Text color="text.grey" fontSize="sm">
                                            {new Date(scenario.createdAtUtc).toLocaleDateString('pl-PL')}
                                        </Text>
                                    </Table.Cell>
                                    <Table.Cell py={4} px={5} border="none" textAlign="right">
                                        <IconButton
                                            variant="outlined"
                                            size="sm"
                                            aria-label="Scenario details"
                                            onClick={() => navigate(`/scenarios/${scenario.id}`)}
                                        >
                                            <HiArrowRight/>
                                        </IconButton>
                                    </Table.Cell>
                                </Table.Row>
                            ))}
                        </Table.Body>
                    </Table.Root>
                </Box>
            </VStack>
            <AssignScenarioModal
                isOpen={isAssignModalOpen}
                onClose={() => setIsAssignModalOpen(false)}
                agentId={id}
                onAssigned={fetchAgent}
            />
        </VStack>
    );
};

export default ModelDetailPage;
